import React, { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import AppleSignin from 'react-apple-signin-auth';
import { extractErrorMessage } from './utils/extractErrorMessage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

// Check if OAuth is available (not wrapped in try-catch will throw if not in GoogleOAuthProvider)
const isOAuthAvailable = () => {
  try {
    const hostname = window.location.hostname;
    
    // Production domains where OAuth is supported
    const productionDomains = [
      'voxsmyth.com',
      'www.voxsmyth.com',
      'app.voxsmyth.com',
      'voice-catalog-boost.emergent.host',
      'voice-studio.emergent.host',
      'audiobookforge.emergent.host'
    ];
    
    // Development/testing domains
    const developmentDomains = [
      'localhost',
      '127.0.0.1'
    ];
    
    // Check if it's a production domain
    if (productionDomains.some(domain => hostname === domain || hostname.endsWith('.' + domain))) {
      return true;
    }
    
    // Check if it's a development domain
    if (developmentDomains.some(domain => hostname === domain || hostname.includes(domain))) {
      return true;
    }
    
    // Enable OAuth for preview.emergentagent.com domains (for testing)
    if (hostname.endsWith('.preview.emergentagent.com')) {
      console.log('🔧 OAuth enabled for preview environment:', hostname);
      return true;
    }
    
    // For other staging environments, temporarily disable to avoid console errors
    console.log('⚠️ OAuth disabled for hostname:', hostname);
    return false;
  } catch (error) {
    return false;
  }
};

const SocialAuthButtons = ({ onSuccess, onError, style = {} }) => {
  const [loading, setLoading] = useState({ google: false, apple: false, facebook: false });
  
  // If OAuth is not available, show a notice
  if (!isOAuthAvailable()) {
    return (
      <div className="text-center p-4 rounded-lg" style={{background: 'rgba(100, 100, 100, 0.1)', border: '1px solid rgba(100, 100, 100, 0.3)'}}>
        <p className="text-sm text-slate-400" style={{fontFamily: 'Georgia, serif'}}>
          Social sign-in is only available on the production site. Please use email/password authentication.
        </p>
      </div>
    );
  }

  const handleGoogleSuccess = async (response) => {
    setLoading(prev => ({ ...prev, google: true }));
    
    try {
      console.log('🔑 Google authentication successful, token received');
      
      const backendResponse = await fetch(`${BACKEND_URL}/api/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: response.credential }),
      });

      const data = await backendResponse.json();

      if (backendResponse.ok && data.success) {
        console.log('✅ Backend authentication successful');
        onSuccess(data);
      } else {
        console.error('❌ Backend authentication failed:', data);
        onError(extractErrorMessage(data, 'Authentication failed'));
      }
    } catch (error) {
      console.error('❌ Google authentication error:', error);
      onError('Network error during authentication');
    } finally {
      setLoading(prev => ({ ...prev, google: false }));
    }
  };

  const handleGoogleError = (error) => {
    console.error('❌ Google sign-in error:', error);
    
    // Check if it's a CORS error
    if (error && (error.toString().includes('CORS') || error.toString().includes('blocked'))) {
      onError('Google sign-in temporarily unavailable. Please try creating an account with email/password or contact support.');
    } else {
      onError('Google sign-in failed. Please try again or use email/password.');
    }
    
    setLoading(prev => ({ ...prev, google: false }));
  };

  const handleAppleSuccess = async (response) => {
    setLoading(prev => ({ ...prev, apple: true }));
    
    try {
      console.log('🍎 Apple authentication successful, processing response');
      
      const { authorization, user } = response;
      const backendResponse = await fetch(`${BACKEND_URL}/api/auth/apple`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          token: authorization.id_token, 
          user: user // Only provided on first login
        }),
      });

      const data = await backendResponse.json();

      if (backendResponse.ok && data.success) {
        console.log('✅ Apple backend authentication successful');
        onSuccess(data);
      } else {
        console.error('❌ Apple backend authentication failed:', data);
          onError(extractErrorMessage(data, 'Apple authentication failed'));
      }
    } catch (error) {
      console.error('❌ Apple authentication error:', error);
      onError('Network error during Apple authentication');
    } finally {
      setLoading(prev => ({ ...prev, apple: false }));
    }
  };

  const handleAppleError = (error) => {
    console.error('❌ Apple sign-in error:', error);
    onError('Apple sign-in failed');
    setLoading(prev => ({ ...prev, apple: false }));
  };

  const handleFacebookLogin = async () => {
    setLoading(prev => ({ ...prev, facebook: true }));
    try {
      // Use Facebook SDK login
      await new Promise((resolve, reject) => {
        if (!window.FB) {
          reject(new Error('Facebook SDK not loaded'));
          return;
        }
        window.FB.login((response) => {
          if (response.authResponse) {
            resolve(response.authResponse.accessToken);
          } else {
            reject(new Error('Facebook login cancelled'));
          }
        }, { scope: 'public_profile,email' });
      }).then(async (accessToken) => {
        const backendResponse = await fetch(`${BACKEND_URL}/api/auth/facebook`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ access_token: accessToken }),
        });
        const data = await backendResponse.json();
        if (backendResponse.ok && data.success) {
          console.log('✅ Facebook auth successful');
          onSuccess(data);
        } else {
          onError(extractErrorMessage(data, 'Facebook authentication failed'));
        }
      });
    } catch (error) {
      console.error('❌ Facebook auth error:', error);
      if (error.message !== 'Facebook login cancelled') {
        onError('Facebook sign-in failed. Please try again.');
      }
    } finally {
      setLoading(prev => ({ ...prev, facebook: false }));
    }
  };

  const buttonStyle = {
    width: '100%',
    padding: '12px 24px',
    borderRadius: '8px',
    border: 'none',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontFamily: 'Georgia, serif',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    marginBottom: '12px',
    ...style
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Only render Google Sign-In if OAuth is available */}
      {isOAuthAvailable() ? (
        <div style={buttonStyle}>
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            size="large"
            width={typeof window !== 'undefined' ? Math.min(400, window.innerWidth - 64) : 400}
            theme="outline"
            shape="rectangular"
            logo_alignment="left"
            text="signin_with"
            disabled={loading.google}
          />
        </div>
      ) : (
        <div className="text-center p-4 rounded-lg" style={{background: 'rgba(100, 100, 100, 0.1)', border: '1px solid rgba(100, 100, 100, 0.3)'}}>
          <p className="text-sm text-slate-400" style={{fontFamily: 'Georgia, serif'}}>
            Social sign-in temporarily disabled for this environment. Please use email/password authentication.
          </p>
        </div>
      )}

      {/* Apple Sign-In - TEMPORARILY DISABLED */}

      {/* Facebook Sign-In - TEMPORARILY DISABLED (pending Meta App Review) */}
      {/* 
      <div style={{ ...buttonStyle, background: '#000', color: '#fff' }}>
        {loading.apple ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '20px',
              height: '20px',
              border: '2px solid #ffffff40',
              borderTop: '2px solid #fff',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            Signing in with Apple...
          </div>
        ) : (
          <AppleSignin
            authOptions={{
              clientId: 'com.sonosmyth.service', // This will need to be configured
              scope: 'email name',
              redirectURI: `${window.location.origin}/auth/apple/callback`,
              state: 'state',
              usePopup: true,
            }}
            onSuccess={handleAppleSuccess}
            onError={handleAppleError}
            skipScript={false}
            render={(props) => (
              <button
                {...props}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#fff',
                  fontSize: '16px',
                  fontWeight: '600',
                  fontFamily: 'Georgia, serif',
                  cursor: 'pointer',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px'
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09z"/>
                </svg>
                Sign in with Apple
              </button>
            )}
          />
        )}
      </div>
      */}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SocialAuthButtons;